import * as enabledApi from '@shared/api/enabledPlayApi';
import UpdateAuthClientRequest from '@shared/models/authentication/UpdateAuthClientRequest';
import { AvailableCustomControlsModel } from '@shared/models/devices/AvailableCustomControlsModel';
import DeviceTypeModel from '@shared/models/devices/DeviceTypeModel';
import UpdateDeviceTypeRequest from '@shared/models/devices/UpdateDeviceTypeRequest';
import ISearchParams from '@shared/models/ISearchParams';
import InviteOrganizationMemberRequest from '@shared/models/organizations/InviteOrganizationMemberRequest';
import { LicenseKeyFilterTypes } from '@shared/models/organizations/LicenseKeyFilterTypes';
import LicenseKeyModel from '@shared/models/organizations/LicenseKeyModel';
import OrganizationAuthClientModel from '@shared/models/organizations/OrganizationAuthClientModel';
import OrganizationMemberDetailsModel from '@shared/models/organizations/OrganizationMemberDetailsModel';
import OrganizationMemberInviteModel from '@shared/models/organizations/OrganizationMemberInviteModel';
import OrganizationMemberModel from '@shared/models/organizations/OrganizationMemberModel';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import OrganizationPrivateProductModel from '@shared/models/organizations/OrganizationPrivateProductModel';
import { UpdateOrganizationMemberRequest } from '@shared/models/organizations/UpdateOrganizationMemberRequest';
import UpdateOrganizationRequest from '@shared/models/organizations/UpdateOrganizationRequest';
import { ProductModel } from '@shared/models/product/ProductModel';
import InvalidResult from '@shared/models/result/InvalidResult';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import IScopeOption from '@shared/models/scopes/IScopeOption';
import { useEffect, useRef, useState } from 'react';
import { createContainer } from 'unstated-next';
import { publish, subscribe } from '../helpers/EventCallback';
import useStateRef from '../useStateRef';

function useOrganizationContainer() {
    const [memberships, setMemberships, membershipsRef] = useStateRef<OrganizationMemberDetailsModel[]>([]);
    const [orgAuthClients, setOrgAuthClients, orgAuthClientsRef] = useStateRef<OrganizationAuthClientModel[]>([]);
    const [orgDeviceTypes, setOrgDeviceTypes, orgDeviceTypesRef] = useStateRef<DeviceTypeModel[]>([]);
    const [orgAvailableCustomControls, setOrgAvailableCustomControls, orgAvailableCustomControlsRef] = useStateRef<
        AvailableCustomControlsModel[]
    >([]);
    const [orgManagedLicenseKeys, setOrgManagedLicenseKeys, orgManagedKeysLicenseRef] = useStateRef<LicenseKeyModel[]>(
        []
    );
    const [orgManagedOrgs, setOrgManagedOrgs, orgManagedOrgsRef] = useStateRef<OrganizationModel[]>([]);
    const [orgProducts, setOrgProducts, orgProductsRef] = useStateRef<OrganizationPrivateProductModel[]>([]);
    const [scopes, setScopes, scopesRef] = useStateRef<IScopeOption[]>([]);
    const [organizationMembers, setOrganizationMembers, organizationMembersRef] = useStateRef<
        { organizationId: string; members: OrganizationMemberModel[] }[]
    >([]);
    const [invites, setInvites, invitesRef] = useStateRef<
        { organizationId: string; invites: OrganizationMemberInviteModel[] }[]
    >([]);
    const [currentOrg, setCurrentOrg, currentOrgRef] = useStateRef<OrganizationModel | null>(null);
    const currentOrgIdRef = useRef<string | null>();
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading, loadingRef] = useStateRef<boolean>(true);
    const currentUserEmailRef = useRef<string | null>(null);

    const [loadingData, setLoadingData, loadingDataRef] = useStateRef({
        organizationMembers: false,
        organizations: false,
        projects: false,
        licenseKeys: false,
        invites: false,
        scopes: false,
        memberships: false,
        currentOrg: false,
        managedOrgs: false,
        managedKeys: false,
        products: false,
    });

    const handleLoadingDataChange = (key: keyof typeof loadingData, value: boolean) => {
        setLoadingData((prev) => ({ ...prev, [key]: value }));
    };

    const initializeFromRemote = async () => {
        return await loadOrganizations(false);
    };

    const updateMemberships = (
        newMemberships: OrganizationMemberDetailsModel[],
        updateLicenseAccess: boolean = true
    ) => {
        setMemberships(newMemberships);
        if (updateLicenseAccess) {
            // preventing double loading of user license access
            publish('updateUserLicenseAccess');
        }
    };

    const initializeFromStorage = () => {
        try {
            const localMemberships = localStorage.getItem('ORGANIZATIONS');
            if (localMemberships) {
                updateMemberships(JSON.parse(localMemberships));
            }
        } catch (e) {
            console.log(e);
        }
    };

    // Implement your organization-related functions here
    const loadOrganizations = async (updateLicenseAccess: boolean = true) => {
        try {
            setLoading(true);
            const response = await enabledApi.getUserOrganizationMemberships();
            if (response.resultType === 'Ok' && response.data) {
                updateMemberships(response.data, updateLicenseAccess);
                localStorage.setItem('ORGANIZATIONS', JSON.stringify(response.data));
            }
            setLoading(false);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setLoading(false);
            return new UnexpectedResult<OrganizationMemberDetailsModel[]>();
        }
    };

    const getOrganizationDetails = async (organizationId: string) => {
        try {
            setLoading(true);
            const response = await enabledApi.getOrganization(organizationId);
            if (response.resultType !== 'Ok') {
                throw new Error('Organization not found');
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            setLoading(false);
        }
    };

    const loadCurrentOrganizationDetails = async (organizationId: string) => {
        try {
            handleLoadingDataChange('currentOrg', true);
            const response = await enabledApi.getOrganization(organizationId);
            if (response.resultType === 'Ok' && response.data) {
                setCurrentOrg(response.data);
            }
            return response;
        } catch (error) {
            setCurrentOrg(null);
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            handleLoadingDataChange('currentOrg', false);
        }
    };

    const loadOrganizationMembers = async (organizationId: string, searchParams?: ISearchParams) => {
        try {
            setLoading(true);
            const response = await enabledApi.getOrganizationMembers(organizationId, searchParams);

            const newMembers = [...organizationMembers];
            if (response.data) {
                if (organizationMembers.findIndex((m) => m.organizationId === organizationId) === -1) {
                    newMembers.push({ organizationId, members: response.data });
                } else {
                    const index = newMembers.findIndex((m) => m.organizationId === organizationId);
                    newMembers[index].members = response.data;
                }
            }
            setOrganizationMembers(newMembers);

            setLoading(false);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setLoading(false);
            return new UnexpectedResult<OrganizationMemberModel[]>();
        }
    };

    const loadInvites = async (organizationId: string, searchParams?: ISearchParams) => {
        try {
            setLoading(true);
            const response = await enabledApi.getOrganizationInvites(organizationId, searchParams);
            const newInvites = [...invites];
            if (response.data) {
                if (invites.findIndex((i) => i.organizationId === organizationId) === -1) {
                    newInvites.push({ organizationId, invites: response.data });
                } else {
                    const index = newInvites.findIndex((i) => i.organizationId === organizationId);
                    newInvites[index].invites = response.data;
                }
            }
            setInvites(newInvites);

            setLoading(false);
            return response;
        } catch (error) {
            setInvites([]);
            setErrors([...errors, error.message]);
            setLoading(false);
            return new UnexpectedResult();
        }
    };

    const createOrganizationInvite = async (organizationId: string, request: InviteOrganizationMemberRequest) => {
        try {
            setLoading(true);
            const response = await enabledApi.createOrganizationInvite(organizationId, request);
            if (response.resultType === 'Ok') {
                await loadInvites(organizationId);
            }
            setLoading(false);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setLoading(false);
        }
    };

    const deleteOrganizationInvite = async (organizationId: string, inviteId: string) => {
        try {
            setLoading(true);
            const response = await enabledApi.deleteOrganizationInvitation(inviteId);
            if (response.resultType === 'Ok') {
                await loadInvites(organizationId);
            }
            setLoading(false);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setLoading(false);
        }
    };

    const removeMember = async (organizationId: string, memberId: string) => {
        try {
            setLoading(true);
            const response = await enabledApi.removeOrganizationMember(memberId);
            if (response.resultType === 'Ok') {
                await loadOrganizationMembers(organizationId);
                if (response.data?.user.email === currentUserEmailRef.current) {
                    publish('updateUserLicenseAccess'); // update user license access after removing member
                }
            }
            setLoading(false);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setLoading(false);
        }
    };

    const updateMemberRole = async (
        organizationId: string,
        memberId: string,
        request: UpdateOrganizationMemberRequest
    ) => {
        try {
            setLoading(true);
            const response = await enabledApi.updateOrganizationMember(memberId, request);
            if (response.resultType === 'Ok') {
                await loadOrganizationMembers(organizationId);
            }
            setLoading(false);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setLoading(false);
        }
    };

    const updateOrganization = async (organizationId: string, request: UpdateOrganizationRequest) => {
        try {
            setLoading(true);
            const response = await enabledApi.updateOrganization(organizationId, request);
            if (response.resultType === 'Ok') {
                await loadOrganizations();
                return response;
            } else {
                throw new Error('Error updating organization');
            }
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            setLoading(false);
        }
    };

    const acceptOrganizationInvite = async (inviteId: string) => {
        try {
            setLoading(true);
            const response = await enabledApi.acceptOrganizationInvite(inviteId);
            if (response.resultType === 'Ok') {
                await loadOrganizations();
            }
            setLoading(false);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setLoading(false);
            return new InvalidResult<OrganizationMemberModel>(error.message);
        }
    };

    const getOrgAuthClients = async (organizationId: string) => {
        if (!organizationId) {
            setOrgAuthClients([]);
            return new UnexpectedResult<OrganizationAuthClientModel[]>();
        }
        try {
            setLoading(true);
            const response = await enabledApi.getOrgAuthClients(organizationId);
            if (response.resultType === 'Ok' && response.data) {
                setOrgAuthClients(response.data);
            } else {
                setOrgAuthClients([]);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setOrgAuthClients([]);
            return new UnexpectedResult<OrganizationAuthClientModel[]>();
        } finally {
            setLoading(false);
        }
    };
    const createOrgAuthClient = async (organizationId: string, request: UpdateAuthClientRequest) => {
        if (!organizationId || !request) {
            return new UnexpectedResult<OrganizationAuthClientModel>();
        }
        try {
            setLoading(true);
            const response = await enabledApi.createOrgAuthClient(organizationId, request);
            if (response.resultType === 'Ok') {
                await getOrgAuthClients(organizationId);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationAuthClientModel>();
        } finally {
            setLoading(false);
        }
    };

    const updateOrgAuthClient = async (
        organizationId: string,
        orgAuthClientId: string,
        request: UpdateAuthClientRequest
    ) => {
        if (!organizationId || !orgAuthClientId || !request) {
            return new UnexpectedResult<OrganizationAuthClientModel>();
        }
        try {
            setLoading(true);
            const response = await enabledApi.updateOrgAuthClient(organizationId, orgAuthClientId, request);
            if (response.resultType === 'Ok') {
                await getOrgAuthClients(organizationId);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationAuthClientModel>();
        } finally {
            setLoading(false);
        }
    };

    const deleteOrgAuthClient = async (organizationId: string, orgAuthClientId: string) => {
        if (!organizationId || !orgAuthClientId) {
            return new UnexpectedResult();
        }
        try {
            setLoading(true);
            const response = await enabledApi.deleteOrgAuthClient(organizationId, orgAuthClientId);
            if (response.resultType === 'Ok') {
                await getOrgAuthClients(organizationId);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult();
        } finally {
            setLoading(false);
        }
    };

    const rotateClientSecret: any = async (organizationId: string, orgAuthClientId: string) => {
        if (!organizationId || !orgAuthClientId) {
            return new UnexpectedResult();
        }
        try {
            setLoading(true);
            const response = await enabledApi.rotateOrgAuthClientSecret(organizationId, orgAuthClientId);
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult();
        } finally {
            setLoading(false);
        }
    };

    const getOrgDeviceTypes = async (organizationId: string) => {
        if (!organizationId) {
            setOrgDeviceTypes([]);
            return new UnexpectedResult<DeviceTypeModel[]>();
        }
        try {
            setLoading(true);
            const response = await enabledApi.getOrgDeviceTypes(organizationId);
            if (response.resultType === 'Ok' && response.data) {
                setOrgDeviceTypes(response.data);
            } else {
                setOrgDeviceTypes([]);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setOrgDeviceTypes([]);
            return new UnexpectedResult<DeviceTypeModel[]>();
        } finally {
            setLoading(false);
        }
    };

    const createOrgDeviceType = async (organizationId: string, request: UpdateDeviceTypeRequest) => {
        if (!organizationId || !request) {
            return new UnexpectedResult<DeviceTypeModel>();
        }
        try {
            setLoading(true);
            const response = await enabledApi.createOrgDeviceType(organizationId, request);
            if (response.resultType === 'Ok') {
                await getOrgDeviceTypes(organizationId);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<DeviceTypeModel>();
        } finally {
            setLoading(false);
        }
    };

    const updateOrgDeviceType = async (
        organizationId: string,
        orgDeviceTypeId: string,
        request: UpdateDeviceTypeRequest
    ) => {
        if (!organizationId || !orgDeviceTypeId || !request) {
            return new UnexpectedResult<DeviceTypeModel>();
        }
        try {
            setLoading(true);
            const response = await enabledApi.updateOrgDeviceType(organizationId, orgDeviceTypeId, request);
            if (response.resultType === 'Ok') {
                await getOrgDeviceTypes(organizationId);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<DeviceTypeModel>();
        } finally {
            setLoading(false);
        }
    };

    const deleteOrgDeviceType = async (organizationId: string, orgDeviceTypeId: string) => {
        if (!organizationId || !orgDeviceTypeId) {
            return new UnexpectedResult();
        }
        try {
            setLoading(true);
            const response = await enabledApi.deleteOrgDeviceType(organizationId, orgDeviceTypeId);
            if (response.resultType === 'Ok') {
                await getOrgDeviceTypes(organizationId);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult();
        } finally {
            setLoading(false);
        }
    };

    const getScopes = async () => {
        try {
            setLoading(true);
            const response = await enabledApi.getScopes();
            if (response.resultType === 'Ok' && response.data) {
                return response.data;
            }
            return [];
        } catch (error) {
            setErrors([...errors, error.message]);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const updateMembershipsByOrganization = async (organization: OrganizationModel) => {
        const localMemberships = membershipsRef.current;

        const index = localMemberships.findIndex((m) => m.organizationId === organization.id);
        if (index > 0) {
            localMemberships[index].organization = organization;
            updateMemberships(localMemberships);
        } else {
            // new organization
            await loadOrganizations(); // this gets all the membership details too
        }
    };

    const activateNewOrgLicenseKey = async (request: NewOrganizationWithKeyRequest) => {
        try {
            setLoading(true);
            const response = await enabledApi.postNewOrgActivateLicenseKey(request);
            if (response.resultType === 'Ok' && response.data) {
                // update memberships
                await updateMembershipsByOrganization(response.data);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            setLoading(false);
        }
    };

    const activateExistingOrgLicenseKey = async (organizationId: string, request: ActivateLicenseKeyRequest) => {
        try {
            setLoading(true);
            const response = await enabledApi.postExistingOrgActivateLicenseKey(organizationId, request);
            if (response.resultType === 'Ok' && response.data) {
                // update memberships
                await updateMembershipsByOrganization(response.data);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            setLoading(false);
        }
    };

    const validateLicenseKey = async (licenseKey: string) => {
        try {
            const response = await enabledApi.validateLicenseKey(licenseKey);
            return response;
        } catch (error) {
            setErrors((prev) => [...prev, error.message]);
            return null;
        }
    };

    const getTrialUpgradeLink = async (organizationId: string, trialId: string) => {
        let url: string | null = null;
        try {
            setLoading(true);
            const response = await enabledApi.getTrialUpgradeLink(organizationId, trialId);
            if (response.resultType == 'Ok' && response.data) {
                url = response.data.url;
            }
            return url;
        } catch (error) {
            setErrors([...errors, error.message]);
            return url;
        } finally {
            setLoading(false);
        }
    };

    const getTrialUpgradeLinkForProduct = async (organizationId: string, trialId: string, productId: string) => {
        let url: string | null = null;
        try {
            setLoading(true);
            const response = await enabledApi.getTrialUpgradeLinkForProduct(organizationId, trialId, productId);
            if (response.resultType == 'Ok' && response.data) {
                url = response.data.url;
            }
            return url;
        } catch (error) {
            setErrors([...errors, error.message]);
            return url;
        } finally {
            setLoading(false);
        }
    };

    const getBillingLink = async (organizationId: string) => {
        let url: string | null = null;
        try {
            setLoading(true);
            const response = await enabledApi.getBillingLink(organizationId);
            if (response.resultType == 'Ok' && response.data) {
                url = response.data.url;
            }
            return url;
        } catch (error) {
            setErrors([...errors, error.message]);
            return url;
        } finally {
            setLoading(false);
        }
    };

    const getBillingLinkForOrgByProduct = async (organizationId: string, productId: string) => {
        let url: string | null = null;
        try {
            setLoading(true);
            const response = await enabledApi.getBillingLinkForOrgByProduct(organizationId, productId);
            if (response.resultType == 'Ok' && response.data) {
                url = response.data.url;
            }
            return url;
        } catch (error) {
            setErrors([...errors, error.message]);
            return url;
        } finally {
            setLoading(false);
        }
    };

    const getBillingLinkForManagedOrg = async (organizationId: string, managedOrgId: string) => {
        let url: string | null = null;
        try {
            setLoading(true);
            const response = await enabledApi.getBillingLinkForManagedOrg(organizationId, managedOrgId);
            if (response.resultType == 'Ok' && response.data) {
                url = response.data.url;
            }
            return url;
        } catch (error) {
            setErrors([...errors, error.message]);
            return url;
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableProducts = async () => {
        try {
            setLoading(true);
            const response = await enabledApi.getPublicProducts();

            if (response.resultType === 'Ok' && response.data) {
                return response.data;
            } else {
                return [] as ProductModel[];
            }
        } catch (error) {
            setErrors([...errors, error.message]);
            return [] as ProductModel[];
        } finally {
            setLoading(false);
        }
    };

    const getOrganizationMembership = (organizationId?: string) => {
        if (!organizationId) {
            return undefined;
        }
        return membershipsRef.current.find((member) => member.organizationId === organizationId);
    };

    const searchOrgManageLicenseKeys = async (
        organizationId: string,
        searchParams: ISearchParams,
        filter: LicenseKeyFilterTypes
    ) => {
        try {
            let response = null;
            if (filter === 'available') {
                response = await getOrgManagedLicenseKeysAvailable(organizationId, searchParams);
            } else if (filter === 'used') {
                response = await getOrgManagedLicenseKeysUsed(organizationId, searchParams);
            } else {
                response = await getOrgManagedLicenseKeys(organizationId, searchParams);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<LicenseKeyModel[]>();
        }
    };

    const getOrgManagedLicenseKeys = async (
        organizationId: string,
        searchParams?: ISearchParams
    ): Promise<IResult<LicenseKeyModel[]>> => {
        try {
            setLoading(true);
            const response = await enabledApi.getOrgManagedLicenseKeys(organizationId, searchParams);
            if (response.resultType === 'Ok' && response.data) {
                setOrgManagedLicenseKeys(response.data);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<LicenseKeyModel[]>();
        } finally {
            setLoading(false);
        }
    };

    const getOrgManagedLicenseKeysAvailable = async (
        organizationId: string,
        searchParams?: ISearchParams
    ): Promise<IResult<LicenseKeyModel[]>> => {
        try {
            setLoading(true);
            const response = await enabledApi.getOrgManagedLicenseKeysAvailable(organizationId, searchParams);
            if (response.resultType === 'Ok' && response.data) {
                setOrgManagedLicenseKeys(response.data);
            } else {
                setOrgManagedLicenseKeys([]);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<LicenseKeyModel[]>();
        } finally {
            setLoading(false);
        }
    };

    const getOrgManagedLicenseKeysUsed = async (
        organizationId: string,
        searchParams?: ISearchParams
    ): Promise<IResult<LicenseKeyModel[]>> => {
        try {
            setLoading(true);
            const response = await enabledApi.getOrgManagedLicenseKeysUsed(organizationId, searchParams);
            if (response.resultType === 'Ok' && response.data) {
                setOrgManagedLicenseKeys(response.data);
            } else {
                setOrgManagedLicenseKeys([]);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<LicenseKeyModel[]>();
        } finally {
            setLoading(false);
        }
    };

    const getOrgManagedOrgsAll = async (
        organizationId: string,
        searchParams?: ISearchParams
    ): Promise<IResult<OrganizationModel[]>> => {
        try {
            setLoading(true);
            const response = await enabledApi.getOrgManagedOrgsAll(organizationId, searchParams);
            if (response.resultType === 'Ok' && response.data) {
                setOrgManagedOrgs(response.data);
            } else {
                setOrgManagedOrgs([]);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationModel[]>();
        } finally {
            setLoading(false);
        }
    };

    const createManagedOrg = async (
        organizationId: string,
        newOrganization: NewOrganizationWithKeyRequest
    ): Promise<IResult<OrganizationModel>> => {
        try {
            setLoading(true);
            const response = await enabledApi.createManagedOrg(organizationId, newOrganization);
            if (response.resultType === 'Ok' && response.data) {
                await getOrgManagedOrgsAll(organizationId);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            setLoading(false);
        }
    };

    const getOrganizationProducts = async (
        organizationId: string,
        locale: string,
        searchParams?: ISearchParams
    ): Promise<IResult<OrganizationPrivateProductModel[]>> => {
        if (!organizationId) {
            setOrgProducts([]);
            return new UnexpectedResult<OrganizationPrivateProductModel[]>();
        }
        try {
            setLoading(true);
            const response = await enabledApi.getOrgProducts(organizationId, locale, searchParams);
            if (response.resultType === 'Ok' && response.data) {
                setOrgProducts(response.data);
            } else {
                setOrgProducts([]);
            }
            return response;
        } catch (error) {
            setErrors([...errors, error.message]);
            setOrgProducts([]);
            return new UnexpectedResult<OrganizationPrivateProductModel[]>();
        } finally {
            setLoading(false);
        }
    };

    const getOrgAvailableCustomControls = async (organizationId: string, locale: string): Promise<boolean> => {
        if (!organizationId) {
            setOrgAvailableCustomControls([]);
            return false;
        }
        let newAvailableCustomControls: AvailableCustomControlsModel[] = [];
        try {
            setLoading(true);
            const response = await getOrgDeviceTypes(organizationId);
            if (response.resultType === 'Ok' && response.data) {
                for (const deviceType of response.data) {
                    const controlsResponse = await enabledApi.getDeviceTypeCustomControls(deviceType.id, locale);
                    if (controlsResponse.resultType === 'Ok' && controlsResponse.data) {
                        if (controlsResponse.data.controls && controlsResponse.data.controls.length > 0) {
                            newAvailableCustomControls.push({
                                deviceId: deviceType.id,
                                deviceTypeId: deviceType.id,
                                name: deviceType.name,
                                controls: controlsResponse.data.controls.filter((control) => control.isProfileControl),
                            });
                        }
                    }
                }
                setOrgAvailableCustomControls(newAvailableCustomControls);
            } else {
                setOrgAvailableCustomControls([]);
            }
            return true;
        } catch (error) {
            setErrors([...errors, error.message]);
            setOrgAvailableCustomControls([]);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getOrganizationProductLink = async (organizationId: string, productId: string) => {
        let url: string | null = null;
        try {
            setLoading(true);
            const response = await enabledApi.getOrgProductLink(organizationId, productId);
            if (response.resultType == 'Ok' && response.data) {
                url = response.data.url;
            }
            return url;
        } catch (error) {
            setErrors([...errors, error.message]);
            return url;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const unsubscribeCurrentUserUpdated = subscribe(
            'userUpdated',
            (data) => (currentUserEmailRef.current = data.user.email)
        );

        return () => {
            unsubscribeCurrentUserUpdated();
        };
    }, []);

    return {
        initializeFromRemote,
        initializeFromStorage,
        memberships,
        membershipsRef,
        organizationMembers,
        organizationMembersRef,
        invites,
        invitesRef,
        errors,
        setErrors,
        loading,
        loadingRef,
        setLoading,
        loadingData,
        loadingDataRef,
        handleLoadingDataChange,
        loadOrganizations,
        loadOrganizationMembers,
        loadInvites,
        createOrganizationInvite,
        deleteOrganizationInvite,
        acceptOrganizationInvite,
        removeMember,
        updateMemberRole,
        updateOrganization,
        getOrgAuthClients,
        createOrgAuthClient,
        updateOrgAuthClient,
        rotateClientSecret,
        deleteOrgAuthClient,
        orgAuthClients,
        orgAuthClientsRef,
        setOrgAuthClients,
        getOrgDeviceTypes,
        setOrgDeviceTypes,
        createOrgDeviceType,
        updateOrgDeviceType,
        deleteOrgDeviceType,
        orgDeviceTypes,
        orgDeviceTypesRef,
        currentOrg,
        currentOrgRef,
        getOrganizationDetails,
        loadCurrentOrganizationDetails,
        getScopes,
        currentOrgIdRef,
        scopes,
        setScopes,
        scopesRef,
        updateMembershipsByOrganization,
        activateNewOrgLicenseKey,
        activateExistingOrgLicenseKey,
        getTrialUpgradeLink,
        getTrialUpgradeLinkForProduct,
        getBillingLink,
        getBillingLinkForOrgByProduct,
        getBillingLinkForManagedOrg,
        loadAvailableProducts,
        orgProducts,
        orgProductsRef,
        getOrganizationProducts,
        getOrganizationProductLink,
        getOrganizationMembership,
        orgManagedKeysLicenseRef,
        orgManagedLicenseKeys,
        searchOrgManageLicenseKeys,
        getOrgManagedLicenseKeys,
        getOrgManagedLicenseKeysAvailable,
        getOrgManagedLicenseKeysUsed,
        orgManagedOrgs,
        orgManagedOrgsRef,
        getOrgManagedOrgsAll,
        createManagedOrg,
        validateLicenseKey,
        orgAvailableCustomControls,
        orgAvailableCustomControlsRef,
        getOrgAvailableCustomControls,
    };
}

const OrganizationContainer = createContainer(useOrganizationContainer);
export default OrganizationContainer;
